<template>
  <onboarding-form-layout
    :title="$t('incremental_onboarding.business_address.title')"
    :description="$t('incremental_onboarding.business_address.description')"
    :loading="submitting"
    :show-previous="true"
    :errors="Object.values(getErrors)"
    @proceed="proceed"
    :prefilled="isPrefilled"
    @previous="goBack"
  >
    <SelectField
      :label="$t('incremental_onboarding.business_address.fields.country.label')"
      :searchable="true"
      :clearable="true"
      :value="getCountry"
      :show-mandatory="true"
      :options="getCountriesEnabledForCbPayments()"
      :on-update="changeCountry"
      :disabled="true"
      :error="getErrors[BusinessAddressFields.COUNTRY]"
      select-classname="country-select"
      field-classname="country-field"
      data-testid="country-field"
    />

    <InputField
      :label="$t('incremental_onboarding.business_address.fields.addressLine1.label')"
      :value="getField(BusinessAddressFields.LINE1)"
      id="address-line-1"
      :show-mandatory="true"
      data-testid="address-line-1"
      :set-value="handleInputChange(BusinessAddressFields.LINE1)"
      :error="getErrors[BusinessAddressFields.LINE1]"
      :max-length="getMax(getApiPathOfField(BusinessAddressFields.LINE1))"
      class="fs-mask"
    />

    <InputField
      :label="$t('incremental_onboarding.business_address.fields.addressLine2.label')"
      :value="getField(BusinessAddressFields.LINE2)"
      id="address-line-2"
      data-testid="address-line-2"
      :set-value="handleInputChange(BusinessAddressFields.LINE2)"
      :error="getErrors[BusinessAddressFields.LINE2]"
      :max-length="getMax(getApiPathOfField(BusinessAddressFields.LINE2))"
      class="fs-mask"
    />

    <div class="city-state-zip s-flex s-gap-4">
      <InputField
        :label="$t('incremental_onboarding.business_address.fields.city.label')"
        :value="getField(BusinessAddressFields.CITY)"
        id="city"
        :show-mandatory="true"
        data-testid="city"
        class-name="s-flex-1"
        :set-value="handleInputChange(BusinessAddressFields.CITY)"
        :error="getErrors[BusinessAddressFields.CITY]"
        :max-length="getMax(getApiPathOfField(BusinessAddressFields.CITY))"
      />

      <InputField
        v-if="stateOptions.length === 0"
        :label="$t('incremental_onboarding.business_address.fields.state.label')"
        :value="getField(BusinessAddressFields.STATE)"
        id="state"
        data-testid="state"
        class-name="s-flex-1"
        :set-value="handleInputChange(BusinessAddressFields.STATE)"
        :error="getErrors[BusinessAddressFields.STATE]"
        :max-length="getMax(getApiPathOfField(BusinessAddressFields.STATE))"
      />

      <SelectField
        v-else
        :label="$t('incremental_onboarding.business_address.fields.state.label')"
        :searchable="true"
        :clearable="false"
        :options="stateOptions"
        :show-mandatory="true"
        :on-update="(value) => updateFieldFromValue({key: BusinessAddressFields.STATE, value})"
        :error="getErrors[BusinessAddressFields.STATE]"
        :value="getField(BusinessAddressFields.STATE)"
        select-classname="state-select"
        class="s-flex-1"
        data-testid="state-select"
      />

      <InputField
        :label="$t('incremental_onboarding.business_address.fields.zipCode.label')"
        :value="getField(BusinessAddressFields.ZIP)"
        :set-value="handleInputChange(BusinessAddressFields.ZIP)"
        :error="getErrors[BusinessAddressFields.ZIP]"
        id="zip-code"
        :show-mandatory="true"
        :max-length="10"
        data-testid="zip-code"
        class-name="s-flex-1"
      />
    </div>
  </onboarding-form-layout>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import InputField from "@/components/common/InputField";
import validations from "@/app/validators/api/openapi";
import SelectField from "@/components/common/SelectField.vue";
import { CBPaymentsEnabledCountries } from "@/data/country-code-list";
import StateCodeList from "@/data/state-code-list";
import { BusinessAddressFields } from "@/store/modules/incremental-onboarding/incrementalOnboardingbusinessAddress";
import merchantOnboardingApi from "@/api/merchantOnboardingApi";
import {OnboardingFormMixin} from "@/components/incremental-onboarding/OnboardingFormMixin";
import OnboardingFormLayout from "@/components/incremental-onboarding/OnboardingFormLayout.vue";
import {EssentialFormField} from "@/store/modules/incremental-onboarding/essentials";
import FullStoryHelper from '@/app/utils/fullstory';
import {BUSINESS_ADDRESS_PROCEED} from '@/app/utils/fullstory/constants';

export default {
  name: 'IncrementalOnboardingBusinessAddress',
  components: { SelectField, InputField, OnboardingFormLayout },
  mixins: [OnboardingFormMixin],
  data: () => ({
    stateOptions: [],
  }),
  computed: {
    BusinessAddressFields() {
      return BusinessAddressFields
    },
    ...mapGetters('incrementalOnboardingBusinessAddress', [
      'getErrors', 'getCountry', 'getField', 'getFieldFormatted',
      'getApiPathOfField', 'getIncrementalOnboardingData',
      'isDirty', 'isPrefilled'
    ]),
  },
  mounted() {
    if (this.getCountry !== null) {
      this.onCountryChange(this.getCountry)
    }
  },
  methods: {
    ...mapActions('incrementalOnboardingBusinessAddress',
        ['updateFieldFromValue', 'updateFieldFromEvent',
          'validate', 'setErrors', 'resetDirty'
        ]),
    ...mapActions('essentials', {
      updateEssentialsFormValue: 'updateFieldFromValue'
    }),
    getMax: key => validations.createMerchant[key].length.maximum,
    getCountriesEnabledForCbPayments: () => CBPaymentsEnabledCountries,

    async proceed() {
      const apiCall = async () => {
        return await merchantOnboardingApi.updateBusinessAddressInMultiStepMerchantOnboarding(
            this.getMerchantId,
            this.getIncrementalOnboardingData
        );
      };

      // Emit FullStory event - this is always an update since merchant already exists
      FullStoryHelper.event(BUSINESS_ADDRESS_PROCEED);

      await this.submitForm(apiCall);
    },

    goBack() {
      this.$emit('previous');
    },

    changeCountry(countryCode) {
      this.updateFieldFromValue({key: BusinessAddressFields.COUNTRY, value: countryCode})
      this.updateEssentialsFormValue({key: EssentialFormField.BUSINESS_COUNTRY, value: countryCode})
      this.onCountryChange(countryCode)
    },

    onCountryChange(countryCode) {
      if (StateCodeList[countryCode]) {
        const statesList = StateCodeList[countryCode];
        this.stateOptions = Object.keys(statesList).map(stateName => ({
          displayText: stateName,
          value: statesList[stateName],
        }));
      } else {
        this.stateOptions = [];
      }
    },
  },
}
</script>
